import React, { useEffect, useState, useContext, useMemo } from "react";
import { Tabs, Tab, PageContent, Page, PageHeader, Box, Text, DataTable, Select, Spinner, Button, Layer, Form, FormField, TextInput, Heading, Card, CardBody, Header, MaskedInput, CheckBox, FileInput, Avatar, ResponsiveContext, Data, Toolbar, DataSearch, DataFilters, DataTableColumns, DataSummary } from 'grommet';
import { useSelector, useDispatch } from "react-redux";
import { adminActions } from "../redux/actions";
import { Edit, Link, StatusGoodSmall, StatusUnknownSmall, Trash, Close, } from "grommet-icons";
import { adminServices, analyticsService, competetiveService } from "../services";
import { NotificationContext } from "../components/admin/NotificationContext";
import { LogAdminEventTypes, LogCompetitiveAdminEvents } from "../redux/constants/logEvents";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";


const exportToExcel = (inData, fileName) => {
    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";
    let input = [];
    inData.forEach(element => {
        input.push(element);
    });
    const ws = XLSX.utils.json_to_sheet(input);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: fileType });
    FileSaver.saveAs(data, fileName + fileExtension);
};

// shared across every admin table so mask defs, options mapping, export/delete logic aren't duplicated per component
const maskForJustWords = [
    {
        regexp: /^[a-zA-Z -]+$/,
        placeholder: 'example',
    },
];
const maskForJustNumbers = [
    {
        regexp: /^[0-9]+$/,
        placeholder: 'example',
    },
];

const columnsToOptions = (columns) =>
    columns.filter(({ header }) => header).map(({ header, property }) => ({
        property,
        label: header
    }));

const getExportSelection = (data, selected) =>
    selected ? data.filter((item) => selected.indexOf(item.id) !== -1) : data;

const validateDeleteConfirmation = (value) =>
    value === 'DELETE' ? undefined : { message: 'Text does not match "DELETE"', status: 'error' };

const useSession = () => useSelector((state) => state.session);

// generic "type DELETE to confirm" layer reused by every admin table's delete action
const DeleteConfirmationLayer = ({ title, message, confirmLabel, onCancel, onSubmit }) => {
    const [cancel, setCancel] = useState('');
    return (
        <Layer onClickOutside={onCancel} onEsc={onCancel}>
            <Card width="medium">
                <CardBody gap="medium">
                    <Header
                        direction="column"
                        align="start"
                        gap="xxsmall"
                        pad={{ horizontal: 'xxsmall' }}
                    >
                        <Heading level={2} margin="none">
                            {title}
                        </Heading>
                    </Header>
                    <Text>
                        {message}
                    </Text>
                    <Box
                        // Padding used to prevent focus from being cutoff
                        pad={{ horizontal: 'xxsmall' }}
                    >
                        <Form
                            validate="blur"
                            onChange={setCancel}
                            messages={{
                                required: 'This is a required field.',
                            }}
                            onSubmit={({ value, touched }) => onSubmit({ value, touched })}
                        >
                            <FormField
                                label="Type DELETE to verify"
                                name="delete"
                                htmlFor="delete"
                                required
                                validate={() => validateDeleteConfirmation(cancel.delete)}
                            >
                                <TextInput id="delete" name="delete" placeholder="DELETE" />
                            </FormField>
                            <Box
                                justify="end"
                                gap="medium"
                                margin={{ top: 'medium', bottom: 'small' }}
                                direction="row"
                            >
                                <Button label="Cancel" onClick={onCancel} />
                                <Button label={confirmLabel} primary type="submit" />
                            </Box>
                        </Form>
                    </Box>
                </CardBody>
            </Card>
        </Layer>
    );
};

// shared list view so DataTable isn't remounted every time its parent re-renders
const AdminResultsTable = ({ columns, selected, setSelected }) => (
    <Box flex overflow="auto">
        <DataTable
            aria-describedby="servers-heading"
            columns={columns}
            pin
            primaryKey="id"
            sortable
            onSelect={nextSelected => setSelected([...nextSelected])}
            select={selected}
        />
    </Box>
);

export const AdminDataManager = () => {

    const TabContent = ({ ...rest }) => (
        <Box pad={{ vertical: 'medium' }}>
            <Box background="background-front" pad="medium" round="medium" {...rest} />
        </Box>
    );
    return <>

        <Page>
            <PageContent>
                <PageHeader title={"Admin Data Manager"} />
                <Box>
                    <Tabs justify="start">
                        <Tab title="Competitor">
                            <TabContent>
                                <CompetitorDataTable />
                            </TabContent>
                        </Tab>
                        <Tab title="Competitor Products">
                            <TabContent>
                                <CompetitorProductsTable />
                            </TabContent>
                        </Tab>
                        <Tab title="Resources">
                            <TabContent>
                                <ResourcesTable />
                            </TabContent>
                        </Tab>
                        <Tab title="Resource Types">
                            <TabContent>
                                <ResourcesTypes />
                            </TabContent>
                        </Tab>
                        {/* <Tab title="Single Click Types">
                            <TabContent>
                                <Text>Coming soon...</Text>
                            </TabContent>
                        </Tab>
                        <Tab title="Single Click Types Resource">
                            <TabContent>
                                <Text>Coming soon...</Text>
                            </TabContent>
                        </Tab> */}
                    </Tabs>
                </Box>
            </PageContent>
        </Page>
    </>
}

export const CompetitorDataTable = () => {
    const { showNotification } = useContext(NotificationContext)
    const dispatch = useDispatch();
    const session = useSession();
    const size = useContext(ResponsiveContext);
    useEffect(() => {
        if (session && session.user && session.user.access) {
            dispatch(adminActions.getAllCompetitors(session.user.access));
        }
    }, [session, dispatch]);
    const cometitorData = useSelector((state) => {
        return state.adminDataManager.admin_competitors
    })
    const [formValues, setFormValue] = useState({
        'competitor_name': '',
        'competitor_thumbnail': '',
        'is_enabled': false,
        'order': 0
    })
    const [addFormValues, setAddFormValue] = useState({
        'competitor_name': '',
        'competitor_thumbnail': '',
        'is_enabled': false,
        'order': 0
    })
    const [onClickedId, setOnClicledId] = useState('')
    const columns = useMemo(() => [
        { property: 'id', header: 'Id', size: "small", primary: true },
        {
            property: 'competitor_name',
            header: 'Name',
            size: 'small',
            render: datum => <Text key={datum.competitor_name}>{datum.competitor_name}</Text>
        },

        {
            property: 'order',
            header: 'Order',
            size: 'small',
            render: datum => <Text key={datum.order}>{datum.order}</Text>
        },
        {
            property: 'competitor_thumbnail',
            header: 'Thumbnail',
            size: 'small',
            render: datum =>
                <Avatar key={datum.competitor_thumbnail} round size="small" src={datum.competitor_thumbnail} />
        },
        {
            property: 'is_enabled',
            header: 'Enabled',
            size: 'small',
            render: datum => datum.is_enabled ? <><StatusGoodSmall color="status-ok" /></> : <><StatusUnknownSmall color="status-unknown" />
            </>
        },
        {
            property: 'created_at',
            header: 'Created Date',
            size: 'small',
            render: datum => <Text key={datum.created_at}>{datum.created_at}</Text>
        },
        {
            property: 'updated_at',
            header: 'Updated Date',
            size: 'small',
            render: datum => <Text key={datum.updated_at}>{datum.updated_at}</Text>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Edit />} onClick={() => {
                            setOnClicledId(dataum.id)
                            setPreview(dataum.competitor_thumbnail)
                            setFormValue({ 'competitor_name': dataum.competitor_name, 'competitor_thumbnail': dataum.competitor_thumbnail, 'is_enabled': dataum.is_enabled, 'order': dataum.order })
                            setIsLayer(true)
                        }} />
                    </Box>
                </>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Trash />} onClick={() => {
                            setOnClicledId(dataum.id)
                            setIsDeleteLayer(true)
                            setValueToBeDeleted(dataum)
                        }} />
                    </Box>
                </>
        }
    ], [])
    const options = useMemo(() => columnsToOptions(columns), [columns])

    const [isLayer, setIsLayer] = useState(false)
    const [isDeleteLayer, setIsDeleteLayer] = useState(false)
    const [onAddClicked, setIsAddClicked] = useState(false)
    const onChange = values => {
        setFormValue(values);
    }
    const onChangeInAdd = values => {
        setAddFormValue(values)
    }
    const handleFileChange = (event) => {
        const newFile = event.target.files?.[0]
        if (newFile) {
            setFile(newFile)
            const preViewUrl = URL.createObjectURL(newFile);
            setPreview(preViewUrl)
        }
    }
    const onCompetitorAdd = ({ value, touched }) => {
        const formData = new FormData();
        if (file) {
            formData.append("competitor_thumbnail", file)
        }
        formData.append('competitor_name', value.competitor_name)
        formData.append('is_enabled', value.is_enabled)
        formData.append('order', value.order)
        try {
            adminServices.addCompetitor(formData, session.user.access).then((response) => {
                showNotification('Competitor Added Succeessfully', 'normal')
                analyticsService.logCompetitiveAdminEvent(session.user.access, LogCompetitiveAdminEvents.COMPETITOR, LogAdminEventTypes.ADD, response && response.id)
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllCompetitors(session.user.access));
                }
            }).catch((error) => {
                showNotification("Error while updating competitor details \n" + error.toString(), 'critical')
            })
        } catch (error) {
            showNotification("Error while updating competitor details \n" + error.toString(), 'critical')
        } finally {
            setIsLayer(false)
        }
    }
    const onCompetitorUpdate = ({ value, touched }) => {
        const formData = new FormData();
        if (file) {
            formData.append("competitor_thumbnail", file)
        }
        formData.append('competitor_name', value.competitor_name)
        formData.append('is_enabled', value.is_enabled)
        formData.append('order', value.order)
        try {
            adminServices.updateCompetitor(onClickedId, formData, session.user.access).then((response) => {
                analyticsService.logCompetitiveAdminEvent(session.user.access, LogCompetitiveAdminEvents.COMPETITOR, LogAdminEventTypes.UPDATE, onClickedId)
                showNotification('Competitor Updated Succeessfully', 'normal')
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllCompetitors(session.user.access));
                }
            }).catch((error) => {
                showNotification("Error while updating competitor details \n" + error.toString(), 'critical')
            })
        } catch (error) {
            showNotification("Error while updating competitor details \n" + error.toString(), 'critical')
        } finally {
            setIsLayer(false)
        }
    };
    const onDeleteSubmit = () => {
        const id = valueToBeDeleted.id;
        const access = session.user.access;
        setIsDeleteLayer(false)
        // log first so the log row still links to the competitor; logging never rejects
        analyticsService.logCompetitiveAdminEvent(access, LogCompetitiveAdminEvents.COMPETITOR, LogAdminEventTypes.DELETE, id)
            .then(() => adminServices.deleteCompetitor(id, access))
            .then(() => showNotification('Competitor Deleted Succeessfully', 'normal'))
            .catch((error) => showNotification(error.status === 404 ? "Competitor was already deleted." : error.message || String(error), 'critical'))
            .finally(() => dispatch(adminActions.getAllCompetitors(access)))
    }
    const [numFiles, setNumfiles] = useState(0);
    const properties = {
        'competitor_name': { label: "Name" },
        'is_enabled': { label: "Is Enabled ?" },
    }
    const [valueToBeDeleted, setValueToBeDeleted] = useState({})
    const [selected, setSelected] = useState([]);
    const [preview, setPreview] = useState('')
    const [file, setFile] = useState(null)
    const [isAddLayer, setIsAddLayer] = useState(false)
    return <>
        {cometitorData.data ? <Data data={cometitorData.data} properties={properties}>
            <Toolbar gap="medium">
                <Toolbar>
                    <DataSearch responsive />
                    <DataFilters drop properties={{
                        is_enabled: {
                            label: "Is Enabled?",
                            options: [
                                { label: 'Active', value: true },
                                { label: 'Inactive', value: false }
                            ]
                        }
                    }} />
                    <DataTableColumns drop tip="Configure Columns" options={options} />
                </Toolbar>
                <Box flex />
                <Button label="Export" onClick={() => {
                    if (cometitorData) {
                        exportToExcel(getExportSelection(cometitorData.data, selected), "competitors")
                    }
                }} />
                <Button secondary label="Add Competitor" onClick={() => {
                    setIsAddLayer(true)
                    setPreview(null)
                }} />
            </Toolbar>
            <DataSummary />
            {/* <DataSummary /> */}
            <AdminResultsTable columns={columns} selected={selected} setSelected={setSelected} />
        </Data> : cometitorData.requestInProgress ? <Spinner /> : <> <Text>No data found </Text> </>
        }
        {isLayer && <Layer onClickOutside={() => {
            setIsAddClicked(false)
            setIsLayer(false)
        }} onEsc={() => {
            setIsAddClicked(false)
            setIsLayer(false)
        }}>
            <Box pad="medium">
                <Box direction="row" align="start" gap="small">
                    <Box gap="medium">
                        <Heading level={2} margin="none">
                            Competitor Update
                        </Heading>
                        <Form
                            validate="blur"
                            value={formValues}
                            messages={{ required: 'This is a required field.' }}
                            onChange={nextValue => onChange(nextValue)}
                            onSubmit={({ value, touched }) => onCompetitorUpdate({ value, touched })}
                            method="post"
                        >
                            <FormField
                                label="Competitor Name"
                                htmlFor="competitor-company-name"
                                name="competitor_name"
                                required
                            >
                                <MaskedInput id="competitor-company-name" name="competitor_name" mask={maskForJustWords} />
                            </FormField>
                            <FormField
                                label="Order"
                                htmlFor="order-for-company"
                                name="order"
                                required
                            >
                                <MaskedInput id="order-for-company" name="order" mask={maskForJustNumbers} />
                            </FormField>
                            <FormField
                                label="Is Enabled ?"
                                htmlFor="is-company-enabled"
                                name="is_enabled"
                            >
                                <CheckBox toggle id="is-company-enabled" name="is_enabled" label="Is Enabled ?" />
                            </FormField>
                            <FormField
                                label="Thumbnail"
                                htmlFor="icon-for-company"
                                name="competitor_thumbnail"
                                required
                            >
                                <Box direction="row">
                                    {preview && (<Avatar round size="medium" src={preview} />)}
                                    <FileInput
                                        messages={{
                                            dropPrompt: 'Drag and drop',
                                            browse: numFiles > 0 ? 'Replace File' : 'Select File'
                                        }}
                                        id="icon-for-company"
                                        onChange={handleFileChange}
                                        name="competitor_thumbnail"
                                        accept="image/*"
                                    />
                                </Box>
                            </FormField>
                            <Box
                                align={!['xsmall', 'small'].includes(size) ? 'start' : undefined}
                                margin={{ top: 'medium', bottom: 'small' }}
                            >
                                <Button label={onAddClicked ? "Add" : "Update"} primary type="submit" />
                            </Box>
                        </Form>
                    </Box>
                    <Button
                        icon={<Close />}
                        onClick={() => {
                            setIsLayer(false)
                            setIsAddClicked(false)
                        }}
                        a11yTitle={`You are in a layer containing a properties of competitors to update.`}
                        autoFocus
                    />
                </Box>
            </Box>
        </Layer>}
        {isAddLayer && <Layer onClickOutside={() => {
            setIsAddLayer(false)
        }} onEsc={() => {
            setIsAddLayer(false)
        }}>
            <Box pad="medium">
                <Box direction="row" align="start" gap="small">
                    <Box gap="medium">
                        <Heading level={2} margin="none">
                            Add Competitor
                        </Heading>
                        <Form
                            validate="blur"
                            value={addFormValues}
                            messages={{ required: 'This is a required field.' }}
                            onChange={nextValue => onChangeInAdd(nextValue)}
                            onSubmit={({ value, touched }) => onCompetitorAdd({ value, touched })}
                            method="post"
                        >
                            <FormField
                                label="Competitor Name"
                                htmlFor="competitor-company-name"
                                name="competitor_name"
                                required
                            >
                                <MaskedInput id="competitor-company-name" name="competitor_name" mask={maskForJustWords} />
                            </FormField>
                            <FormField
                                label="Order"
                                htmlFor="order-for-company"
                                name="order"
                                required
                            >
                                <MaskedInput id="order-for-company" name="order" mask={maskForJustNumbers} />
                            </FormField>
                            <FormField
                                label="Is Enabled ?"
                                htmlFor="is-company-enabled"
                                name="is_enabled"
                            >
                                <CheckBox toggle id="is-company-enabled" name="is_enabled" label="Is Enabled ?" />
                            </FormField>
                            <FormField
                                label="Thumbnail"
                                htmlFor="icon-for-company"
                                name="competitor_thumbnail"
                                required
                            >
                                <Box direction="row">
                                    {preview && (<Avatar round size="medium" src={preview} />)}
                                    <FileInput
                                        messages={{
                                            dropPrompt: 'Drag and drop',
                                            browse: numFiles > 0 ? 'Replace File' : 'Select File'
                                        }}
                                        id="icon-for-company"
                                        onChange={handleFileChange}
                                        name="competitor_thumbnail"
                                        accept="image/*"
                                    />
                                </Box>
                            </FormField>
                            <Box
                                align={!['xsmall', 'small'].includes(size) ? 'end' : undefined}
                                margin={{ top: 'medium', bottom: 'small' }}
                            >
                                <Button label={"Add"} primary type="submit" />
                            </Box>
                        </Form>
                    </Box>
                    <Button
                        icon={<Close />}
                        onClick={() => {
                            setIsAddLayer(false)
                        }}
                        a11yTitle={`You are in a layer containing a properties of competitors to add.`}
                        autoFocus
                    />
                </Box>
            </Box>
        </Layer>}
        {isDeleteLayer && (
            <DeleteConfirmationLayer
                title="Delete Competitor"
                message="Are you sure you want to delete this Competitor? Doing so will erase all related the data(Competitor Products and its resources)."
                confirmLabel="Delete competitor"
                onCancel={() => setIsDeleteLayer(false)}
                onSubmit={onDeleteSubmit}
            />
        )}
    </>
}

export const CompetitorProductsTable = () => {
    const dispatch = useDispatch();
    const session = useSession();
    useEffect(() => {
        if (session && session.user && session.user.access) {
            dispatch(adminActions.getAllCompetitorProducts(session.user.access));
            dispatch(adminActions.getAllProductTypes(session.user.access));
        }
    }, [session, dispatch]);
    const competitorProductList = useSelector((state) => {
        return state.adminDataManager.admin_competitor_products
    })
    const competitorsList = useSelector((state) => {
        return state.adminDataManager.admin_competitors
    })
    const productTypes = useSelector((state) => {
        return state.adminDataManager.admin_product_types
    })
    const [productTypeOptions, setProductTypesOptions] = useState([])
    const [competitorsOption, setCompetitorsOptions] = useState([])

    const size = useContext(ResponsiveContext);
    const { showNotification } = useContext(NotificationContext)
    const [updateFormValues, setUpdateFormValues] = useState({
        'competitor_product_name': '',
        'order': 0,
        'competitor_product_group': 0,
        'product_type': 0
    })
    const [onUpdateClicked, setOnUpdateClicked] = useState(false)
    const [onUpdateClickedId, setOnUpdateClickedId] = useState(null)
    const onUpdateChanged = values => {
        setUpdateFormValues(values)
    }
    const onUpdateSubmit = ({ value, touched }) => {
        let formData = new FormData()
        Object.entries(value).forEach(([key, value]) => {
            formData.append(key, value)
        })
        try {
            adminServices.updateCompetitorProducts(onUpdateClickedId, formData).then((response) => {
                analyticsService.logCompetitiveAdminEvent(session.user.access, LogCompetitiveAdminEvents.COMPETITOR_PRODUCT, LogAdminEventTypes.UPDATE, null, onUpdateClickedId)
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllCompetitorProducts(session.user.access));
                }
                showNotification('Competitor product succefully updated', 'normal')
            }).catch((error) => {
                showNotification("Error while updating competitor product details. \n Error: " + error.toString(), 'critical')
            })

        } catch (e) {
            showNotification("Error while updating competitor product details. \n Error: " + e.toString(), 'critical')
        } finally {
            setOnUpdateClicked(false)
        }
    }
    const [onDeleteClicked, setOnDeleteClicked] = useState(false)
    const [onDeleteClickedId, setOnDeleteClickedId] = useState(null)
    const onDeleteSubmit = () => {
        const id = onDeleteClickedId;
        const access = session.user.access;
        setOnDeleteClicked(false)
        analyticsService.logCompetitiveAdminEvent(access, LogCompetitiveAdminEvents.COMPETITOR_PRODUCT, LogAdminEventTypes.DELETE, null, id)
            .then(() => adminServices.deleteCompetitorProduct(id, access))
            .then(() => showNotification('Competitor product deleted successfully', 'normal'))
            .catch((error) => showNotification(error.status === 404 ? "Competitor product was already deleted." : error.message || String(error), 'critical'))
            .finally(() => dispatch(adminActions.getAllCompetitorProducts(access)))
    }

    useEffect(() => {
        if (competitorsList.data) {
            let opt = competitorsList.data.map(item => ({
                label: item.competitor_name,
                value: item.id
            }))
            setCompetitorsOptions(opt)
        }
        if (productTypes.data) {
            let opts = productTypes.data.map(item => ({
                label: item.product_type,
                value: item.id
            }))
            setProductTypesOptions(opts)
        }


    }, [competitorsList, productTypes])
    const [addFormValues, setAddFormValues] = useState({
        'competitor_product_name': '',
        'order': 0,
        'competitor_product_group': 0,
        'product_type': 0
    })
    const [onAddClicked, setOnAddClicked] = useState(false)
    const onAddChanged = values => {
        setAddFormValues(values)
    }
    const onAddSubmit = ({ value, touched }) => {
        const formData = new FormData()
        Object.entries(value).forEach(([key, value]) => {
            formData.append(key, value)
        })
        try {
            adminServices.addCompetitorProduct(formData, session.user.access).then((response) => {
                analyticsService.logCompetitiveAdminEvent(session.user.access, LogCompetitiveAdminEvents.COMPETITOR_PRODUCT, LogAdminEventTypes.ADD, value.competitor_product_group, response && response.id)
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllCompetitorProducts(session.user.access));
                }
                showNotification('Competitor product succefully added', 'normal')
            }).catch((error) => {
                showNotification("Error while updating competitor product details. \n Error: " + error.toString(), 'critical')
            })

        } catch (error) {
            showNotification("Error while updating competitor product details. \n Error: " + error.toString(), 'critical')
        } finally {
            setOnAddClicked(false)
        }
    }
    const properties = {
        'competitor_product_group': { label: 'Company' },
    }
    const columns = useMemo(() => [
        { property: 'id', header: 'Id', size: "small", primary: true },
        {
            property: 'competitor_product_name',
            header: 'Name',
            size: 'small',
            render: datum => <Text key={datum.competitor_product_name}>{datum.competitor_product_name}</Text>
        },
        {
            property: 'competitor_product_group',
            header: 'Company',
            size: 'small',
            render: datum => <Text key={datum.competitor_product_group}>{datum.competitor_product_group}</Text>
        },
        {
            property: 'order',
            header: 'Order',
            size: 'small',
            render: datum => <Text key={datum.order}>{datum.order}</Text>
        },
        {
            property: 'created_at',
            header: 'Created Date',
            size: 'small',
            render: datum => <Text key={datum.created_at}>{datum.created_at}</Text>
        },
        {
            property: 'updated_at',
            header: 'Updated Date',
            size: 'small',
            render: datum => <Text key={datum.updated_at}>{datum.updated_at}</Text>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Edit />} onClick={() => {
                            setOnUpdateClickedId(dataum.id)
                            const competitorMatch = competitorsList.data.find(competitor => competitor.competitor_name === dataum.competitor_product_group)
                            const productTypeMatch = productTypes.data.find(type => type.product_type === dataum.product_type)
                            if (competitorMatch && productTypeMatch) {
                                setUpdateFormValues(
                                    {
                                        'competitor_product_name': dataum.competitor_product_name,
                                        'competitor_product_group': competitorMatch.id,
                                        'product_type': productTypeMatch.id, 'order': dataum.order
                                    })
                            }

                            setOnUpdateClicked(true)
                        }} />
                    </Box>
                </>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Trash />} onClick={() => {
                            setOnDeleteClicked(true)
                            setOnDeleteClickedId(dataum.id)
                        }} />
                    </Box>
                </>
        }
    ], [competitorsList, productTypes])
    const options = useMemo(() => columnsToOptions(columns), [columns])
    const [selected, setSelected] = useState([]);

    return <>
        {competitorProductList.data ? <>
            <Data data={competitorProductList.data} properties={properties}>
                <Toolbar>
                    <Toolbar>
                        <DataSearch responsive />
                        <DataFilters drop />
                        <DataTableColumns drop tip="Configure Columns" options={options} />
                    </Toolbar>
                    <Box flex />
                    <Button label="Export" onClick={() => {
                        if (competitorProductList) {
                            exportToExcel(getExportSelection(competitorProductList.data, selected), "CompetitorProducts")
                        }
                    }} />
                    <Button secondary label="Add Competitor Product" onClick={() => {
                        setOnAddClicked(true)
                    }} />

                </Toolbar>
                <DataSummary />
                <AdminResultsTable columns={columns} selected={selected} setSelected={setSelected} />
            </Data>
        </> : competitorProductList.requestInProgress ? <Spinner /> : <Text>No data found!</Text>
        }
        {
            <>
                {onUpdateClicked && <Layer onClickOutside={() => {
                    setOnUpdateClicked(false)
                }} onEsc={() => { setOnUpdateClicked(false) }}>
                    <Box pad="medium">
                        <Box direction="row" align="start" gap="small">
                            <Box gap="medium">
                                <Heading level={2} margin="none">
                                    Competitor Product Update
                                </Heading>
                                <Form
                                    validate="blur"
                                    value={updateFormValues}
                                    messages={{ required: 'This is a required field.' }}
                                    onChange={nextValue => onUpdateChanged(nextValue)}
                                    onSubmit={({ value, touched }) => onUpdateSubmit({ value, touched })}
                                    method="post"
                                >
                                    <FormField
                                        label="Competitor Product Name"
                                        htmlFor="competitor-product-name"
                                        name="competitor_product_name"
                                        required
                                    >
                                        <MaskedInput id="competitor_product_name" name="competitor_product_name" mask={maskForJustWords} />
                                    </FormField>
                                    <FormField
                                        label="Order"
                                        htmlFor="order-for-company"
                                        name="order"
                                        required
                                    >
                                        <MaskedInput id="order-for-company" name="order" mask={maskForJustNumbers} />
                                    </FormField>
                                    <FormField label="Competitor Company" name="competitor_name">
                                        <Select id="competitor_name" name='competitor_name'
                                            options={competitorsOption}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={updateFormValues.competitor_product_group}
                                            onChange={({ option }) => {
                                                setUpdateFormValues(prev => ({ ...prev, competitor_product_group: option.value }))
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Product Type" name="product_type">
                                        <Select id="product_type" name='product_type'
                                            options={productTypeOptions}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={updateFormValues.product_type}
                                            onChange={({ option }) => {
                                                setUpdateFormValues(prev => ({ ...prev, product_type: option.value }))
                                            }}
                                        />
                                    </FormField>
                                    <Box
                                        align={!['xsmall', 'small'].includes(size) ? 'start' : undefined}
                                        margin={{ top: 'medium', bottom: 'small' }}
                                    >
                                        <Button label={"Update"} primary type="submit" />
                                    </Box>
                                </Form>
                            </Box>
                            <Button
                                icon={<Close />}
                                onClick={() => {
                                    setOnUpdateClicked(false)
                                }}
                                a11yTitle={`You are in a layer containing a properties of competitors to update.`}
                                autoFocus
                            />
                        </Box>
                    </Box>
                </Layer>}
            </>
        }
        {
            <>
                {onAddClicked && <Layer onClickOutside={() => {
                    setOnAddClicked(false)
                }} onEsc={() => { setOnAddClicked(false) }}>
                    <Box pad="medium">
                        <Box direction="row" align="start" gap="small">
                            <Box gap="medium">
                                <Heading level={2} margin="none">
                                    Add New Competitor Product
                                </Heading>
                                <Form
                                    validate="blur"
                                    value={addFormValues}
                                    messages={{ required: 'This is a required field.' }}
                                    onChange={nextValue => onAddChanged(nextValue)}
                                    onSubmit={({ value, touched }) => onAddSubmit({ value, touched })}
                                    method="post"
                                >
                                    <FormField
                                        label="Competitor Product Name"
                                        htmlFor="competitor-product-name"
                                        name="competitor_product_name"
                                        required
                                    >
                                        <MaskedInput id="competitor_product_name" name="competitor_product_name" mask={maskForJustWords} placeholder="Enter product name" />
                                    </FormField>
                                    <FormField
                                        label="Order"
                                        htmlFor="order-for-company"
                                        name="order"
                                        required
                                    >
                                        <MaskedInput id="order-for-company" name="order" mask={maskForJustNumbers} />
                                    </FormField>
                                    <FormField label="Competitor Company" name="competitor_name">
                                        <Select id="competitor_product_group" name='competitor_product_group'
                                            required
                                            options={competitorsOption}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={addFormValues.competitor_product_group}
                                            onChange={({ option }) => {
                                                setAddFormValues(prev => ({ ...prev, competitor_product_group: option.value }))
                                            }}
                                            placeholder="Select Company"
                                        />
                                    </FormField>
                                    <FormField label="Product Type" name="product_type">
                                        <Select id="product_type" name='product_type'
                                            required
                                            options={productTypeOptions}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={addFormValues.product_type}
                                            onChange={({ option }) => {
                                                setAddFormValues(prev => ({ ...prev, product_type: option.value }))
                                            }}
                                            placeholder="Select Type"
                                        />
                                    </FormField>
                                    <Box
                                        align={!['xsmall', 'small'].includes(size) ? 'start' : undefined}
                                        margin={{ top: 'medium', bottom: 'small' }}
                                    >
                                        <Button label="Add" primary type="submit" />
                                    </Box>
                                </Form>
                            </Box>
                            <Button
                                icon={<Close />}
                                onClick={() => {
                                    setOnAddClicked(false)
                                }}
                                a11yTitle={`You are in a layer containing a properties of competitors to update.`}
                                autoFocus
                            />
                        </Box>
                    </Box>
                </Layer>}
            </>
        }
        {onDeleteClicked && (
            <DeleteConfirmationLayer
                title="Delete Competitor Product"
                message="Are you sure you want to delete this Competitor Product? Doing so will erase all related the data(including its resources)."
                confirmLabel="Delete Product"
                onCancel={() => setOnDeleteClicked(false)}
                onSubmit={onDeleteSubmit}
            />
        )}
    </>
}

export const ResourcesTable = () => {
    const dispatch = useDispatch();
    const session = useSession();
    const size = useContext(ResponsiveContext);
    const { showNotification } = useContext(NotificationContext)
    useEffect(() => {
        if (session && session.user && session.user.access) {
            dispatch(adminActions.getAllResources(session.user.access));
            dispatch(adminActions.getAllResourceTypes(session.user.access))
        }
    }, [session, dispatch]);
    const resourcesList = useSelector((state) => {
        return state.adminDataManager.admin_resources
    })
    const resourceTypesList = useSelector((state) => {
        return state.adminDataManager.admin_resource_types
    })
    const competitorsList = useSelector((state) => {
        return state.adminDataManager.admin_competitors
    })

    const [competitorProductList, setCompetitorProductList] = useState([])
    const [onUpdateClicked, setOnUpdateClicked] = useState(false)
    const [onAddClicked, setOnAddClicked] = useState(false)
    const [onDeleteClicked, setOnDeleteClicked] = useState(false)
    const [updateFormValues, setUpdateFormValues] = useState({
        "resource_title": "",
        "link": "",
        "is_enabled": true,
        "order": 0,
        "resource_content_type": 0,
        "competitor": 0,
        "competitors_products": 0
    })
    const [addFormValues, setAddFormValues] = useState({
        "resource_title": "",
        "link": "",
        "is_enabled": true,
        "order": 0,
        "resource_content_type": 0,
        "competitor": 0,
        "competitors_products": 0
    })
    const onUpdateFormValuesChanged = values => {
        setUpdateFormValues(values)
    }
    const onAddFormValuesChanged = values => {
        setAddFormValues(values)
    }
    const [resourceTypeOptions, setResourceTypesOptions] = useState()
    const [competitorOptions, setCompetitorOptions] = useState([])
    const [competitorProductsOptions, setCompetitorProductsOptions] = useState([])

    const [updateSelectedId, setUpdateSelectedId] = useState(0)

    const [onDeleteClickedId, setOnDeleteClickedId] = useState(null)

    const [selected, setSelected] = useState([]);

    useEffect(() => {
        if (competitorsList.data) {
            let opt = competitorsList.data.map(item => ({
                label: item.competitor_name,
                value: item.id
            }))
            setCompetitorOptions(opt)
        }
        if (resourceTypesList.data) {
            let opts = resourceTypesList.data.map(item => ({
                label: item.resource_type,
                value: item.id
            }))
            setResourceTypesOptions(opts)
        }
    }, [competitorsList, resourceTypesList])

    useEffect(() => {
        if (!updateFormValues.competitor) return;
        const loadCompetitorProduct = async () => {
            competetiveService.getCompetitorProducts(updateFormValues.competitor, session.user.access).then((response) => {
                setCompetitorProductList(response)
            })
        }
        loadCompetitorProduct();
    }, [updateFormValues.competitor, session])
    useEffect(() => {
        if (!addFormValues.competitor) return;
        const loadCompetitorProduct = async () => {
            competetiveService.getCompetitorProducts(addFormValues.competitor, session.user.access).then((response) => {
                setCompetitorProductList(response)
            })
        }
        loadCompetitorProduct();
    }, [addFormValues.competitor, session])

    useEffect(() => {
        if (Array.isArray(competitorProductList) && competitorProductList.length > 0) {
            let opts = competitorProductList.map(item => ({
                label: item.competitor_product_name,
                value: item.id
            }))
            setCompetitorProductsOptions(opts)
        }
        if (competitorProductList.length === 0) {
            setCompetitorProductsOptions([])
        }

    }, [competitorProductList, addFormValues])


    const onUpdateSubmit = ({ value, touched }) => {
        let formData = new FormData()
        Object.entries(value).forEach(([key, value]) => {
            formData.append(key, value)
        })
        try {
            adminServices.updateResource(updateSelectedId, formData, session.user.access).then((response) => {
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllResources(session.user.access));
                }
                analyticsService.logCompetitiveAdminEvent(session.user.access, LogCompetitiveAdminEvents.RESOURCES, LogAdminEventTypes.UPDATE, null, null, null, updateSelectedId)
                showNotification("Resource Successfully updated", "normal")
            }).catch((error) => {
                showNotification("Error while updating resource \n" + error.toString(), "critical")
            })
        }
        catch (e) {
            let errMsg = e.toString()
            showNotification(errMsg, "critical")
        }
        finally {
            setOnUpdateClicked(false)
        }
    }

    const onAddSubmit = ({ value, touched }) => {
        let formData = new FormData()
        Object.entries(value).forEach(([key, value]) => {
            formData.append(key, value)
        })
        try {
            adminServices.addResource(formData, session.user.access).then((response) => {
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllResources(session.user.access));
                }
                showNotification("Resource Successfully added", "normal")
                analyticsService.logCompetitiveAdminEvent(session.user.access, LogCompetitiveAdminEvents.RESOURCES, LogAdminEventTypes.ADD, value.competitor, value.competitors_products, value.resource_content_type, response && response.id)

            }).catch((error) => {
                showNotification(error.toString(), "critical")
            })

        }
        catch (e) {
            let errMsg = e.toString()
            showNotification(errMsg, "critical")
        }
        finally {
            setOnAddClicked(false)
        }
    }

    const onDeleteSubmit = () => {
        const id = onDeleteClickedId;
        const access = session.user.access;
        setOnDeleteClicked(false)
        analyticsService.logCompetitiveAdminEvent(access, LogCompetitiveAdminEvents.RESOURCES, LogAdminEventTypes.DELETE, null, null, null, id)
            .then(() => adminServices.deleteResource(id, access))
            .then(() => showNotification('Resource deleted successfully', 'normal'))
            .catch((error) => showNotification('Resource delete failed: ' + (error.message || String(error)), 'critical'))
            .finally(() => dispatch(adminActions.getAllResources(access)))
    }

    const columns = useMemo(() => [
        { property: 'id', header: 'Id', size: "xsmall", primary: true },
        {
            property: 'resource_title',
            header: 'Name',
            size: 'small',
            render: datum => <Text key={datum.resource_title}>{datum.resource_title}</Text>
        },
        {
            property: 'competitor',
            header: 'Company',
            size: 'small',
            render: datum => <Text key={datum.competitor}>{datum.competitor}</Text>
        },
        {
            property: 'competitors_products',
            header: 'Product',
            size: 'small',
            render: datum => <Text key={datum.competitors_products}>{datum.competitors_products}</Text>
        },
        {
            property: 'order',
            header: 'Order',
            size: 'xsmall',
            render: datum => <Text key={datum.order}>{datum.order}</Text>
        },
        {
            property: 'resource_content_type',
            header: 'Resource Type',
            size: 'small',
            render: datum => <Text key={datum.resource_content_type}>{datum.resource_content_type}</Text>
        },
        {
            property: 'updated_at',
            header: 'Updated Date',
            size: 'small',
            render: datum => <Text key={datum.updated_at}>{datum.updated_at}</Text>
        },
        {
            property: 'is_enabled',
            header: 'Enabled',
            size: 'small',
            render: datum => datum.is_enabled ? <><StatusGoodSmall color="status-ok" /></> : <><StatusUnknownSmall color="status-unknown" />
            </>
        },
        {
            property: 'link',
            header: 'Link',
            size: 'xsmall',
            render: datum => <Button icon={<Link />} onClick={() => {
                window.open(datum.link)
            }} />
            // render: datum => <Text key={datum.resource_content_type}>{datum.resource_content_type}</Text>
        },
        {
            property: 'created_at',
            header: 'Created Date',
            size: 'small',
            render: datum => <Text key={datum.created_at}>{datum.created_at}</Text>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Edit />} onClick={() => {
                            setUpdateSelectedId(dataum.id)
                            const competitorMatch = competitorsList.data.find(competitor => competitor.competitor_name === dataum.competitor)
                            const resourceTypeMatch = resourceTypesList.data.find(type => type.resource_type === dataum.resource_content_type)
                            if (!competitorMatch) {
                                showNotification("Unable to load this resource's competitor for editing", 'critical')
                                return;
                            }
                            // open right away with what's already loaded; competitors_products fills in once the network call resolves
                            setUpdateFormValues({
                                "resource_title": dataum.resource_title,
                                "link": dataum.link,
                                "is_enabled": dataum.is_enabled,
                                "order": dataum.order,
                                "resource_content_type": resourceTypeMatch ? resourceTypeMatch.id : '',
                                "competitor": competitorMatch.id,
                                "competitors_products": ''
                            })
                            setOnUpdateClicked(true)
                            competetiveService.getCompetitorProducts(competitorMatch.id, session.user.access).then((response) => {
                                setCompetitorProductList(response)
                                const competitorProductMatch = response.find(product => product.competitor_product_name === dataum.competitors_products)
                                if (competitorProductMatch) {
                                    setUpdateFormValues(prev => ({ ...prev, competitors_products: competitorProductMatch.id }))
                                }
                            }).catch((error) => {
                                showNotification("Error while loading competitor products \n" + error.toString(), 'critical')
                            })
                        }} />
                    </Box>
                </>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Trash />} onClick={() => {
                            setOnDeleteClicked(true)
                            setOnDeleteClickedId(dataum.id)
                        }} />
                    </Box>
                </>
        }
    ], [competitorsList, resourceTypesList, session, showNotification])
    const options = useMemo(() => columnsToOptions(columns), [columns])
    const properties = {
        'competitor': { label: 'Company' },
        'competitors_products': { label: "Product" },
        'is_enabled': { label: "Is Enabled ?" },
        'resource_content_type': { label: 'Resource Type' }
    }
    const filterProps = {
        is_enabled: {
            label: "Is Enabled?",
            options: [
                { label: 'Active', value: true },
                { label: 'Inactive', value: false }
            ],
            filter: { control: "CheckBoxGroup" }
        },
        // you can add more filterable fields here
    };
    const normalizeDefaults = (properties) =>
        Object.keys(properties).reduce((acc, key) => {
            acc[key] = []; // always an array so .includes works
            return acc;
        }, {});
    return <>
        {/* {resourcesList.requestInProgress ? <Spinner /> : <DataTable columns={columns} data={resourcesList ? resourcesList.data : null} sort={{ ...sort }} fill gap="small" />} */}
        {resourcesList.data ? <>
            <Data data={resourcesList.data} properties={properties}>
                <Toolbar>
                    <Toolbar>
                        <DataSearch responsive />
                        <DataFilters
                            drop
                            properties={[filterProps]}
                            defaultValues={normalizeDefaults(filterProps)}
                        />
                        <DataTableColumns drop tip="Configure Columns" options={options} />
                    </Toolbar>
                    <Box flex />
                    <Button label="Export" onClick={() => {
                        if (resourcesList) {
                            exportToExcel(getExportSelection(resourcesList.data, selected), "Resources")
                        }
                    }} />
                    <Button secondary label="Add Resource" onClick={() => {
                        setOnAddClicked(true)
                    }} />

                </Toolbar>
                <DataSummary />
                <AdminResultsTable columns={columns} selected={selected} setSelected={setSelected} />
            </Data>
        </> : resourcesList.requestInProgress ? <Spinner /> : <Text> No resources found</Text>}
        {
            <>
                {onUpdateClicked && <Layer onClickOutside={() => {
                    setOnUpdateClicked(false)
                }} onEsc={() => { setOnUpdateClicked(false) }}>
                    <Box pad="medium">
                        <Box direction="row" align="start" gap="small">
                            <Box gap="medium">
                                <Heading level={2} margin="none">
                                    Competitor Product Update
                                </Heading>
                                <Form
                                    validate="blur"
                                    value={updateFormValues}
                                    messages={{ required: 'This is a required field.' }}
                                    onChange={nextValue => onUpdateFormValuesChanged(nextValue)}
                                    onSubmit={({ value, touched }) => onUpdateSubmit({ value, touched })}
                                    method="post"
                                >
                                    <FormField
                                        label="Resource Title"
                                        htmlFor="resource_title"
                                        name="resource_title"
                                        required
                                    >
                                        <MaskedInput id="resource_title" name="resource_title" mask={maskForJustWords} />
                                    </FormField>
                                    <FormField
                                        label="Order"
                                        htmlFor="order"
                                        name="order"
                                        required
                                    >
                                        <MaskedInput id="order" name="order" mask={maskForJustNumbers} />
                                    </FormField>
                                    <FormField label="Competitor Company" name="competitor">
                                        <Select id="competitor" name='competitor'
                                            options={competitorOptions}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={updateFormValues.competitor}
                                            onChange={({ option }) => {
                                                setUpdateFormValues(prev => ({ ...prev, competitor: option.value, competitors_products: '' }))
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Competitor Product" name="competitors_products">
                                        <Select id="competitors_products" name='competitors_products'
                                            options={competitorProductsOptions ? competitorProductsOptions : []}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={updateFormValues.competitors_products}
                                            onChange={({ option }) => {
                                                setUpdateFormValues(prev => ({ ...prev, competitors_products: option.value }))
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Resource Type" name="resource_content_type">
                                        <Select id="resource_content_type" name='resource_content_type'
                                            options={resourceTypeOptions}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={updateFormValues.resource_content_type}
                                            onChange={({ option }) => {
                                                setUpdateFormValues(prev => ({ ...prev, resource_content_type: option.value }))
                                            }}
                                        />
                                    </FormField>
                                    <FormField
                                        label="Link"
                                        htmlFor="resource-link"
                                        name="link"
                                        required
                                    >
                                        <MaskedInput id="link" name="link" />
                                    </FormField>
                                    <FormField
                                        label="Is Enabled ?"
                                        htmlFor="is-company-enabled"
                                        name="is_enabled"
                                    >
                                        <CheckBox toggle id="is_enabled" name="is_enabled" label="Is Enabled ?" />
                                    </FormField>
                                    <Box
                                        align={!['xsmall', 'small'].includes(size) ? 'start' : undefined}
                                        margin={{ top: 'medium', bottom: 'small' }}
                                    >
                                        <Button label={"Update"} primary type="submit" />
                                    </Box>
                                </Form>
                            </Box>
                            <Button
                                icon={<Close />}
                                onClick={() => {
                                    setOnUpdateClicked(false)
                                }}
                                a11yTitle={`You are in a layer containing a properties of competitors to update.`}
                                autoFocus
                            />
                        </Box>
                    </Box>
                    <>
                    </>
                </Layer>}
            </>
        }
        {
            <>
                {onAddClicked && <Layer onClickOutside={() => {
                    setOnAddClicked(false)
                }} onEsc={() => { setOnAddClicked(false) }}>
                    <Box pad="medium">
                        <Box direction="row" align="start" gap="small">
                            <Box gap="medium">
                                <Heading level={2} margin="none">
                                    Resources Add
                                </Heading>
                                <Form
                                    validate="blur"
                                    value={addFormValues}
                                    messages={{ required: 'This is a required field.' }}
                                    onChange={nextValue => onAddFormValuesChanged(nextValue)}
                                    onSubmit={({ value, touched }) => onAddSubmit({ value, touched })}
                                    method="post"
                                >
                                    <FormField
                                        label="Resource Title"
                                        htmlFor="resource_title"
                                        name="resource_title"
                                        required
                                    >
                                        <MaskedInput id="resource_title" name="resource_title" mask={maskForJustWords} />
                                    </FormField>
                                    <FormField
                                        label="Order"
                                        htmlFor="order"
                                        name="order"
                                        required
                                    >
                                        <MaskedInput id="order" name="order" mask={maskForJustNumbers} />
                                    </FormField>
                                    <FormField label="Competitor Company" name="competitor">
                                        <Select id="competitor" name='competitor'
                                            options={competitorOptions}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={addFormValues.competitor}
                                            onChange={({ option }) => {
                                                setAddFormValues(prev => ({ ...prev, competitor: option.value, competitors_products: '' }))
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Competitor Product" name="competitors_products">
                                        <Select id="competitors_products" name='competitors_products'
                                            options={competitorProductsOptions ? competitorProductsOptions : []}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={addFormValues.competitors_products}
                                            onChange={({ option }) => {
                                                setAddFormValues(prev => ({ ...prev, competitors_products: option.value }))
                                            }}
                                        />
                                    </FormField>
                                    <FormField label="Resource Type" name="resource_content_type">
                                        <Select id="resource_content_type" name='resource_content_type'
                                            options={resourceTypeOptions}
                                            labelKey={'label'}
                                            valueKey={{ 'key': 'value', reduce: true }}
                                            value={addFormValues.resource_content_type}
                                            onChange={({ option }) => {
                                                setAddFormValues(prev => ({ ...prev, resource_content_type: option.value }))
                                            }}
                                        />
                                    </FormField>
                                    <FormField
                                        label="Link"
                                        htmlFor="resource-link"
                                        name="link"
                                        required
                                    >
                                        <MaskedInput id="link" name="link" />
                                    </FormField>
                                    <FormField
                                        label="Is Enabled ?"
                                        htmlFor="is-company-enabled"
                                        name="is_enabled"
                                    >
                                        <CheckBox toggle id="is_enabled" name="is_enabled" label="Is Enabled ?" />
                                    </FormField>
                                    <Box
                                        align={!['xsmall', 'small'].includes(size) ? 'start' : undefined}
                                        margin={{ top: 'medium', bottom: 'small' }}
                                    >
                                        <Button label={"Add"} primary type="submit" />
                                    </Box>
                                </Form>
                            </Box>
                            <Button
                                icon={<Close />}
                                onClick={() => {
                                    setOnAddClicked(false)
                                }}
                                a11yTitle={`You are in a layer containing a properties of resrouces to add.`}
                                autoFocus
                            />
                        </Box>
                    </Box>
                    <>
                    </>
                </Layer>}
            </>
        }
        {onDeleteClicked && (
            <DeleteConfirmationLayer
                title="Delete Resource"
                message="Are you sure you want to delete this Resource?"
                confirmLabel="Delete Product"
                onCancel={() => setOnDeleteClicked(false)}
                onSubmit={onDeleteSubmit}
            />
        )}
    </>
}

export const ResourcesTypes = () => {
    const size = useContext(ResponsiveContext);
    const { showNotification } = useContext(NotificationContext)
    const dispatch = useDispatch();
    const session = useSession();
    useEffect(() => {
        if (session && session.user && session.user.access) {
            dispatch(adminActions.getAllResourceTypes(session.user.access));
        }
    }, [session, dispatch]);

    const resourcesTypeList = useSelector((state) => {
        return state.adminDataManager.admin_resource_types
    })
    const [addFormValues, setAddFormValues] = useState({
        'resource_type': '',
        'type_description': '',
        'type_color': ''
    })
    const columns = useMemo(() => [
        { property: 'id', header: 'Id', size: "xxsmall", primary: true },
        {
            property: 'resource_type',
            header: 'Name',
            size: 'small',
            render: datum => <Text key={datum.resource_type}>{datum.resource_type}</Text>
        },
        {
            property: 'type_description',
            header: 'Description',
            size: 'small',
            render: datum => <Text key={datum.type_description}>{datum.type_description}</Text>
        },
        {
            property: 'type_color',
            header: 'Color',
            size: 'xsmall',
            render: datum =>
                <>
                    <Box direction="row" gap="xsmall" align="center">
                        <StatusGoodSmall color={datum.type_color} />
                        <Text key={datum.type_color}>{datum.type_color}</Text>
                    </Box>
                </>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Edit />} onClick={() => {
                            if (dataum) {
                                setOnUpdateClickedId(dataum.id)
                                setUpdateFormValues({ 'resource_type': dataum.resource_type, 'type_description': dataum.type_description ? dataum.type_description : '', 'type_color': dataum.type_color })
                                setOnUpdateClicked(true)
                            }
                        }} />
                    </Box>
                </>
        },
        {
            size: 'xxsmall',
            render: dataum =>
                <>
                    <Box direction="row">
                        <Button icon={<Trash />} onClick={() => {
                            setOnDeleteClickedId(dataum.id)
                            setIsDeleteLayer(true)
                            setValueToBeDeleted(dataum)
                        }} />
                    </Box>
                </>
        }
    ], [])
    const [isAddLayer, setIsAddLayer] = useState(false)
    const [isDeleteLayer, setIsDeleteLayer] = useState(false)
    const [onAddClicked, setIsAddClicked] = useState(false)
    const [onUpdateClicked, setOnUpdateClicked] = useState(false)
    const [onUpdateClickedId, setOnUpdateClickedId] = useState(null)
    const [updateFormValues, setUpdateFormValues] = useState({
        'resource_type': '',
        'type_description': '',
        'type_color': ''
    })
    const onUpdateChanged = values => {
        setUpdateFormValues(values)
    }
    const onUpdateSubmit = ({ value, touched }) => {
        let formData = new FormData()
        Object.entries(value).forEach(([key, value]) => {
            formData.append(key, value)
        })
        try {
            adminServices.updateResourceType(onUpdateClickedId, formData, session.user.access).then((response) => {
                showNotification('Resoource Type succefully updated', 'normal')
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllResourceTypes(session.user.access));
                }
            }).catch((error) => {
                showNotification("Error while updating Resoource Type. \n Error: " + error.toString(), 'critical')
            })

        } catch (e) {
            showNotification("Error while updating Resoource Type. \n Error: " + e.toString(), 'critical')
        } finally {
            setOnUpdateClicked(false)
        }
    }
    const onResourceTypeAdd = ({ value, touched }) => {
        const formData = new FormData();
        formData.append('resource_type', value.resource_type)
        formData.append('type_description', value.type_description)
        formData.append('type_color', value.type_color)
        try {
            adminServices.addResourceType(formData, session.user.access).then((response) => {
                showNotification('Resource Type Added Succeessfully', 'normal')
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllResourceTypes(session.user.access));
                }
            }).catch((error) => {
                showNotification("Error while updating Resource Type \n" + error.toString(), 'critical')
            })
        } catch (error) {
            showNotification("Error while updating Resource Type \n" + error.toString(), 'critical')
        } finally {
            setIsAddClicked(false)
        }
    }
    const [onDeleteClicked, setOnDeleteClicked] = useState(false)
    const [onDeleteClickedId, setOnDeleteClickedId] = useState(null)
    const onDeleteSubmit = ({ value, touched }) => {
        try {
            adminServices.deleteResourceType(onDeleteClickedId, session.user.access).then((response) => {
                showNotification('Resource Type deleted successfully', 'normal')
                if (session && session.user && session.user.access) {
                    dispatch(adminActions.getAllResourceTypes(session.user.access));
                }
            }).catch((error) => {
                showNotification('Resource Type deleted faied with error: \n ' + error.toString(), 'critical')
            })
        } catch (error) {
            showNotification('Resource Type deleted faied with error: \n ' + error.toString(), 'critical')
        } finally {
            setOnDeleteClicked(false)
            setIsDeleteLayer(false)
        }
    }
    const onChangeInAdd = values => {
        setAddFormValues(values)
    }
    const options = useMemo(() => columnsToOptions(columns), [columns])
    const properties = {
        'resource_type': { label: "Name" },
        'type_description': { label: "Description" },
        'type_color': { label: "Color" }
    }
    const [sort, setSort] = useState({ property: 'id', direction: 'asc' });
    const [valueToBeDeleted, setValueToBeDeleted] = useState({})
    return <>

        {resourcesTypeList.data ? <Data data={resourcesTypeList.data} properties={properties}>
            <Toolbar gap="medium">
                <Toolbar>
                    <DataSearch responsive />
                    <DataTableColumns drop tip="Configure Columns" options={options} />
                </Toolbar>
                <Box felx />
                <Button alignSelf="end" justify="end" secondary label="Add Resource type" onClick={() => {
                    setIsAddLayer(true)
                }} />
            </Toolbar>
            <DataSummary />
            <Box flex overflow={true}>
                <DataTable
                    columns={columns}
                    pin
                    primaryKey="id"
                    sortable
                />
            </Box>
        </Data> : resourcesTypeList.requestInProgress ? <Spinner /> : <><Text>No Data found</Text></>
        }
        {
            <>
                {onUpdateClicked && <Layer onClickOutside={() => {
                    setOnUpdateClicked(false)
                }} onEsc={() => { setOnUpdateClicked(false) }}>
                    <Box pad="medium">
                        <Box direction="row" align="start" gap="small">
                            <Box gap="medium">
                                <Heading level={2} margin="none">
                                    Resource Type Update
                                </Heading>
                                <Form
                                    validate="blur"
                                    value={updateFormValues}
                                    messages={{ required: 'This is a required field.' }}
                                    onChange={nextValue => onUpdateChanged(nextValue)}
                                    onSubmit={({ value, touched }) => onUpdateSubmit({ value, touched })}
                                    method="post"
                                >
                                    <FormField
                                        label="Name"
                                        htmlFor="resource-type"
                                        name="resource_type"
                                        required
                                    >
                                        <TextInput id="resource_type" name="resource_type" placeholder={"Resource-Type Name"} />
                                    </FormField>
                                    <FormField
                                        label="Description"
                                        htmlFor="type-desc"
                                        name="type_description"
                                        required
                                    >
                                        <TextInput id="type_description" name="type_description" placeholder={"Enter type description"} />
                                    </FormField>
                                    <FormField
                                        label="Colour"
                                        htmlFor="type_color"
                                        name="type_color"
                                        required
                                    >
                                        <TextInput id="type_color" name="type_color" placeholder="Choose from HPE colors" />
                                    </FormField>
                                    <Box
                                        align={!['xsmall', 'small'].includes(size) ? 'start' : undefined}
                                        margin={{ top: 'medium', bottom: 'small' }}
                                    >
                                        <Button label={"Update"} primary type="submit" />
                                    </Box>
                                </Form>
                            </Box>
                            <Button
                                icon={<Close />}
                                onClick={() => {
                                    setOnUpdateClicked(false)
                                }}
                                a11yTitle={`You are in a layer containing a properties of competitors to update.`}
                                autoFocus
                            />
                        </Box>
                    </Box>
                </Layer>}
            </>
        }
        {isAddLayer && <Layer onClickOutside={() => {
            setIsAddLayer(false)
        }} onEsc={() => {
            setIsAddLayer(false)
        }}>
            <Box pad="medium">
                <Box direction="row" align="start" gap="small">
                    <Box gap="medium">
                        <Heading level={2} margin="none">
                            Add Resource Type
                        </Heading>
                        <Form
                            validate="blur"
                            value={addFormValues}
                            messages={{ required: 'This is a required field.' }}
                            onChange={nextValue => onChangeInAdd(nextValue)}
                            onSubmit={({ value, touched }) => onResourceTypeAdd({ value, touched })}
                            method="post"
                        >
                            <FormField
                                label="Name"
                                htmlFor="resource-type"
                                name="resource_type"
                                required
                            >
                                <TextInput id="resource_type" name="resource_type" placeholder={"Resource-Type Name"} />
                            </FormField>
                            <FormField
                                label="Description"
                                htmlFor="type-desc"
                                name="type_description"
                                required
                            >
                                <TextInput id="type_description" name="type_description" placeholder={"Enter type description"} />
                            </FormField>
                            <FormField
                                label="Colour"
                                htmlFor="type_color"
                                name="type_color"
                                required
                            >
                                <TextInput id="type_color" name="type_color" placeholder="Choose from HPE colors" />
                            </FormField>
                            <Box
                                align={!['xsmall', 'small'].includes(size) ? 'end' : undefined}
                                margin={{ top: 'medium', bottom: 'small' }}
                            >
                                <Button label={"Add"} primary type="submit" />
                            </Box>
                        </Form>
                    </Box>
                    <Button
                        icon={<Close />}
                        onClick={() => {
                            setIsAddLayer(false)
                        }}
                        a11yTitle={`You are in a layer containing a properties of competitors to add.`}
                        autoFocus
                    />
                </Box>
            </Box>
        </Layer>}
        {isDeleteLayer && (
            <DeleteConfirmationLayer
                title="Delete Resource Type"
                message="Are you sure you want to delete this Resource Type? Doing so will erase all related data."
                confirmLabel="Delete Resource type"
                onCancel={() => setIsDeleteLayer(false)}
                onSubmit={onDeleteSubmit}
            />
        )}
        {/* {resourcesList.requestInProgress ? <Spinner /> : <DataTable columns={columns} data={resourcesList ? resourcesList.data : null} sort={{ ...sort }} fill gap="small" />} */}
    </>
}